import React, { useState, Suspense, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Switch, Redirect, useHistory } from 'react-router-dom';
import { GeneralButton, GeneralSelect, LoadingIndicator, SearchBox, TimeSelectorRow } from 'sharedComponents';
import { getTabOptions } from './helpers';
import GatewayEventTableComponent from './shared/GatewayEventTableComponent';
import './style.scss';
import GatewayEventTypeFilter from './GatewayEventsFilter/GatewayEventTypeFilter';
import GatewayEventReportTypeFilter from './GatewayEventsFilter/GatewayEventReportTypeFilter';
import { DownloadLogsModal } from './DownloadLogsModal';
import { apiCallStatus } from 'helpers/constants';
import { IconGatewayFilter, IconCancel, IconInfo, IconDownload } from 'svgIcons/MotionPortalIcons';
import Tippy from '@tippy.js/react';
import { useDebounceApiCall } from 'sharedHooks';
import { routes } from 'routes';
import PropTypes from 'prop-types';
import { INITIAL_PAGE_SIZE, LOAD_MORE_PAGE_SIZE, MAX_SEARCH_LENGTH, MIN_FILENAME_SEARCH_LENGTH, FilterFields, SMART_SENSOR_DCU, APP, SMART_SENSOR, DRIVE_DCU, EventTypeGatewayApplicability, GatewayEventTabType, EventReportType } from './constants';
import colors from 'theme/_colors.scss';
import {
    useEventTypeSelection,
    useTabRouting,
    useGatewayEventFilters
} from './hooks';

const GatewayEventsComponent = (props) => {
    const { t: translate } = useTranslation();
    const history = useHistory();

    // Local state
    const [tabOptions] = useState(getTabOptions(translate));
    const [showFilter, setShowFilter] = useState(false);
    const [previousActiveTab, setPreviousActiveTab] = useState(null);
    // Local state for Report Type filter and Type toggle (All/Log/Report) - SmartSensor DCU only.
    const [selectedReportTypes, setSelectedReportTypes] = useState([]);
    const [selectedTabType, setSelectedTabType] = useState(GatewayEventTabType.ALL);
    const [eventNameError, setEventNameError] = useState('');
    const [userEmailError, setUserEmailError] = useState('');
    const [dcuSerialNumberError, setDcuSerialNumberError] = useState('');
    const [showDownloadLogsModal, setShowDownloadLogsModal] = useState(false);

    // Ref to track if we're currently loading more (prevents multiple simultaneous calls)
    const isLoadingMoreRef = useRef(false);

    // Props destructuring
    const {
        filters,
        gatewayEventLogLoadingStatus,
        nextCursor,
        hasMoreData,
        isLoadingMore
    } = props.model;
    const { setFilters, resetFilters, fetchGatewayEventsLog, loadMoreGatewayEvents } = props.actions;

    // Custom hooks for cleaner logic
    const { activeTabIndex, currentTab, tabRoutes } = useTabRouting(tabOptions);

    const {
        apiSeverityCodes,
        displayedEventTypes,
        handleFilterByEventType,
        resetSelection: resetEventTypeSelection
    } = useEventTypeSelection(filters?.eventTypes, setFilters, currentTab?.eventLogType === SMART_SENSOR_DCU);

    const {
        hasSearchFilters,
        textSearchFields,
        handleSearchFieldChange,
        handleSelectDate
    } = useGatewayEventFilters(filters, setFilters, resetFilters);

    // Derived state
    const isLoading = gatewayEventLogLoadingStatus === apiCallStatus.LOADING;
    const basePath = routes.GatewayManagementGatewayEvents;

    // Tab type constant for conditional rendering
    const isGatewayTab = currentTab?.eventLogType === SMART_SENSOR_DCU;
    const isAppTab = currentTab?.eventLogType === APP;
    const isDriveDCUTab = currentTab?.eventLogType === DRIVE_DCU;
    const isSmartSensorTab = currentTab?.eventLogType === SMART_SENSOR;

    // Reset filters on component mount and cleanup on unmount
    useEffect(() => {
        resetFilters();
        resetEventTypeSelection();
        setEventNameError('');
        setUserEmailError('');
        setDcuSerialNumberError('');
        setSelectedReportTypes([]);
        setSelectedTabType(GatewayEventTabType.ALL);

        return () => {
            resetFilters();
        };
    }, []);

    // Reset filters when switching tabs
    useEffect(() => {
        if (previousActiveTab !== null && previousActiveTab !== activeTabIndex && activeTabIndex !== -1) {
            resetFilters();
            resetEventTypeSelection();
            setEventNameError('');
            setUserEmailError('');
            setDcuSerialNumberError('');
            setSelectedReportTypes([]);
            setSelectedTabType(GatewayEventTabType.ALL);
        }
        setPreviousActiveTab(activeTabIndex);
    }, [activeTabIndex, previousActiveTab, resetFilters, resetEventTypeSelection]);

    // Check if we can make API call
    const canMakeApiCall = useMemo(() => {
        return !!(
            filters?.dateSelection?.from &&
            filters?.dateSelection?.to &&
            activeTabIndex >= 0 &&
            currentTab
        );
    }, [filters?.dateSelection, activeTabIndex, currentTab]);

    // API call function
    const makeApiCall = useCallback(() => {
        if (canMakeApiCall && currentTab) {
            const isGatewayEventSource = currentTab.eventLogType === SMART_SENSOR_DCU;
            // Block API if Gateway tab file name search is less than 5 characters
            if (isGatewayEventSource && filters?.eventName && filters.eventName.length > 0 && filters.eventName.length < MIN_FILENAME_SEARCH_LENGTH) {
                return;
            }
            const requestPayload = {
                dcuSerialNumber: filters?.dcuSerialNumber || '',
                organizationId: filters?.organizationId || 0,
                userEmailAddress: filters?.userEmail || '',
                ...!isGatewayEventSource && { messageTextContains: filters?.eventName || '' },
                eventSource: currentTab.eventLogType,
                timestampFrom: filters?.dateSelection?.from || '',
                timestampTo: filters?.dateSelection?.to || '',
                cursor: null,
                pageSize: INITIAL_PAGE_SIZE,
                severityCodes: apiSeverityCodes,
                ...isGatewayEventSource && {
                    fileNameStartsWith: filters?.eventName || '',
                    ...selectedTabType === GatewayEventTabType.ALL && {
                        reportType: [EventReportType.MAINTENANCE, EventReportType.TRAFFIC_REPORT, EventReportType.STATISTICS, EventReportType.SENSOR_REPORT, EventReportType.APP_LOG]
                    },
                    ...selectedTabType === GatewayEventTabType.LOG && {
                        reportType: [EventReportType.APP_LOG]
                    },
                    ...selectedTabType === GatewayEventTabType.REPORT && {
                        reportType: selectedReportTypes.length > 0
                            ? selectedReportTypes
                            : [EventReportType.MAINTENANCE, EventReportType.TRAFFIC_REPORT, EventReportType.STATISTICS, EventReportType.SENSOR_REPORT]
                    }
                }
            };
            fetchGatewayEventsLog(requestPayload);
        }
    }, [canMakeApiCall, currentTab, filters?.dcuSerialNumber, filters?.eventName, filters?.userEmail, filters?.organizationId, filters?.dateSelection, fetchGatewayEventsLog, apiSeverityCodes, selectedTabType, selectedReportTypes]);

    // Load more function - only called on scroll to bottom
    const loadMoreEvents = useCallback(() => {
        // Prevent multiple simultaneous calls using ref (immediate check)
        if (isLoadingMoreRef.current || !hasMoreData || !nextCursor || !canMakeApiCall || !currentTab) {
            return;
        }

        // Set loading flag immediately to prevent duplicate calls
        isLoadingMoreRef.current = true;

        const isGatewayEventSource = currentTab.eventLogType === SMART_SENSOR_DCU;
        // Block load-more if Gateway tab file name search is less than 5 characters
        if (isGatewayEventSource && filters?.eventName && filters.eventName.length > 0 && filters.eventName.length < MIN_FILENAME_SEARCH_LENGTH) {
            isLoadingMoreRef.current = false;
            return;
        }
        const requestPayload = {
            dcuSerialNumber: filters?.dcuSerialNumber || '',
            organizationId: filters?.organizationId || 0,
            userEmailAddress: filters?.userEmail || '',
            ...!isGatewayEventSource && { messageTextContains: filters?.eventName || '' },
            eventSource: currentTab.eventLogType,
            timestampFrom: filters?.dateSelection?.from || '',
            timestampTo: filters?.dateSelection?.to || '',
            cursor: nextCursor,
            pageSize: LOAD_MORE_PAGE_SIZE,
            severityCodes: apiSeverityCodes,
            ...isGatewayEventSource && {
                fileNameStartsWith: filters?.eventName || '',
                ...selectedTabType === GatewayEventTabType.ALL && {
                    reportType: [EventReportType.MAINTENANCE, EventReportType.TRAFFIC_REPORT, EventReportType.STATISTICS, EventReportType.SENSOR_REPORT, EventReportType.APP_LOG]
                },
                ...selectedTabType === GatewayEventTabType.LOG && {
                    reportType: [EventReportType.APP_LOG]
                },
                ...selectedTabType === GatewayEventTabType.REPORT && {
                    reportType: selectedReportTypes.length > 0
                        ? selectedReportTypes
                        : [EventReportType.MAINTENANCE, EventReportType.TRAFFIC_REPORT, EventReportType.STATISTICS, EventReportType.SENSOR_REPORT]
                }
            }
        };
        loadMoreGatewayEvents(requestPayload);
    }, [canMakeApiCall, currentTab, filters?.dcuSerialNumber, filters?.eventName, filters?.userEmail, filters?.organizationId, filters?.dateSelection, nextCursor, hasMoreData, apiSeverityCodes, loadMoreGatewayEvents, selectedTabType, selectedReportTypes]);

    // Reset loading ref when isLoadingMore state changes
    useEffect(() => {
        if (!isLoadingMore) {
            isLoadingMoreRef.current = false;
        }
    }, [isLoadingMore]);

    // Use debounced API call
    useDebounceApiCall(
        makeApiCall,
        [
            filters?.dateSelection,
            filters?.eventTypes,
            filters?.eventName,
            filters?.gatewaySerialNumber,
            filters?.organizationId,
            filters?.userEmail,
            filters?.dcuSerialNumber,
            activeTabIndex,
            selectedTabType,
            selectedReportTypes
        ],
        {
            textSearchFields,
            textSearchDelay: 700,
            immediateDelay: 0,
            condition: canMakeApiCall
        }
    );

    // Validation functions
    const getSearchErrorMessage = useCallback((value) => {
        if (value && value.length > MAX_SEARCH_LENGTH) {
            return translate('ABB.Powertrain.Frontend.searchLengthValidation');
        }
        return '';
    }, [translate]);

    // Enhanced handleSearchFieldChange with validation
    const handleSearchFieldChangeWithValidation = useCallback((field, value) => {
        let errorMsg = field === FilterFields.EVENT_NAME && isGatewayTab ? '' : getSearchErrorMessage(value);
        // For Gateway tab file name: require minimum 5 characters
        if (field === FilterFields.EVENT_NAME && isGatewayTab && value.length > 0 && value.length < MIN_FILENAME_SEARCH_LENGTH) {
            errorMsg = translate('ABB.Powertrain.Frontend.gatewayEventsFilenameSearchError');
        }

        if (field === FilterFields.EVENT_NAME) {
            setEventNameError(errorMsg);
        } else if (field === FilterFields.DCU_SERIAL_NUMBER) {
            setDcuSerialNumberError(errorMsg);
        } else if (field === FilterFields.USER_EMAIL) {
            setUserEmailError(errorMsg);
        }

        // Gateway tab file name has no max-length restriction — only min 5 chars.
        const isGatewayFileName = field === FilterFields.EVENT_NAME && isGatewayTab;
        if (isGatewayFileName || value.length <= MAX_SEARCH_LENGTH) {
            handleSearchFieldChange(field, value);
        }
    }, [getSearchErrorMessage, handleSearchFieldChange, isGatewayTab, translate]);

    // Toggle a report type in/out of the selected list.
    const handleFilterByReportType = useCallback((reportType) => {
        setSelectedReportTypes(prev =>
            prev.includes(reportType)
                ? prev.filter(t => t !== reportType)
                : [...prev, reportType]
        );
    }, []);

    // Handle Type tab selection (All / Log / Report) - resets sub-filters on type change
    const handleSelectTabType = useCallback((tabType) => {
        setSelectedTabType(tabType);
        setSelectedReportTypes([]);
        resetEventTypeSelection();
    }, [resetEventTypeSelection]);

    // Event handlers
    const handleTabClick = useCallback((route, index) => {
        const targetRoute = tabRoutes[index];
        history.push(targetRoute);
    }, [history, tabRoutes]);

    const toggleFilter = useCallback(() => {
        setShowFilter(prev => !prev);
    }, []);

    const handleResetFilters = useCallback(() => {
        resetFilters();
        resetEventTypeSelection();
        setEventNameError('');
        setUserEmailError('');
        setDcuSerialNumberError('');
        setSelectedReportTypes([]);
        setSelectedTabType(GatewayEventTabType.ALL);
    }, [resetFilters, resetEventTypeSelection]);

    // sorted unique organization options from the generalFilter org list
    const organizationOptions = useMemo(() => {
        const orgs = props.model.organizations || [];
        const options = orgs
            .filter(org => org?.organizationId && org?.name)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(org => ({ value: org.organizationId, label: org.name }));
        return options;
    }, [props.model.organizations]);

    // Filtering is handled backend-side; pass through log as-is.
    const filteredGatewayEventsLog = props.model.gatewayEventsLog;

    const enhancedModel = useMemo(() => ({
        ...props.model,
        gatewayEventsLog: filteredGatewayEventsLog,
        isFilterExpanded: showFilter,
        loadMoreEvents
    }), [props.model, filteredGatewayEventsLog, showFilter, loadMoreEvents]);

    const openDownloadLogsModal = useCallback(() => {
        setShowDownloadLogsModal(true);
    }, []);

    const closeDownloadLogsModal = useCallback(() => {
        setShowDownloadLogsModal(false);
    }, []);

    return (
        <div className='gateway-events'>
            <h2 className='gateway-events__head'>{translate('ABB.Powertrain.Frontend.GatewayEventsGlobalEventLogTitle')}</h2>

            {/* Custom Tab Header */}
            <div className='gateway-events-header'>
                <div className='tab-component toggle'>
                    <div className='tab-header'>
                        {tabOptions.map((tab, index) =>
                            <button
                                key={tab.route}
                                className={`tab-item ${activeTabIndex === index ? 'active' : ''}`}
                                onClick={() => handleTabClick(tab.route, index)}
                                disabled={isLoading}
                            >
                                {tab.title}
                            </button>
                        )}
                    </div>
                </div>
                <div className='ml-auto gateway-events-filters d-flex align-items-flex-end justify-content-space-between'>
                    {(isSmartSensorTab || isDriveDCUTab) &&
                        <div className='filter-event-type'>
                            <GatewayEventTypeFilter
                                selectedStates={displayedEventTypes}
                                selectState={handleFilterByEventType}
                                disabled={isLoading}
                            />
                        </div>
                    }
                    <div className='filter-time-selector'>
                        <TimeSelectorRow
                            fromDate={filters?.dateSelection?.from}
                            toDate={filters?.dateSelection?.to}
                            handleSelectDate={handleSelectDate}
                            label={''}
                            maxDaysRange={90}
                            maxDaysBack={90}
                            preSelectedDays={1}
                            numberOfMonths={2}
                            showLoading={false}
                            footerIntervalText={translate('ABB.Powertrain.Frontend.datePickerMax90DaysNote')}
                            disabled={isLoading}
                        />
                    </div>

                    {/* Filter Toggle Button */}
                    <div className='filter-btn'>
                        <button
                            className={`filter-icon ${showFilter ? 'filter-icon--active' : ''}`}
                            onClick={toggleFilter}
                            disabled={isLoading}
                        >
                            <IconGatewayFilter />
                        </button>
                    </div>
                    {(isGatewayTab || isSmartSensorTab) &&
                        <div className='download-logs'>

                            <GeneralButton data-testid='downloadLogsButton' type='normal' text={translate('ABB.Powertrain.Frontend.downloadLogsLabel')} onClick={openDownloadLogsModal} disabled={isLoading}
                                icon={<IconDownload width={16} height={16} />}
                            />

                        </div>
                    }
                </div>
            </div>

            {/* Filter Section */}
            {showFilter &&
                <div className='filter-section gateway-events-filter'>
                    <div className='filter-wrapper'>

                        {isGatewayTab &&
                            <div className='filter-search-file-name'>
                                <div className='filter-label-with-info'>
                                    <label>{translate('ABB.Powertrain.Frontend.fileName')}</label>
                                    <Tippy
                                        content={translate('ABB.Powertrain.Frontend.gatewayEventsSearchWithFileName')}
                                        theme='light-border'
                                        trigger='mouseenter'
                                        maxWidth='300px'
                                        placement='top'
                                    >
                                        <span className='info-icon-wrapper'>
                                            <IconInfo color={'black'} />
                                        </span>
                                    </Tippy>
                                </div>
                                <SearchBox
                                    id='event-name-search'
                                    className={eventNameError ? 'search-box--error' : ''}
                                    onChange={(searchTerm) => handleSearchFieldChangeWithValidation(FilterFields.EVENT_NAME, searchTerm)}
                                    value={filters?.eventName || ''}
                                    placeholder={translate('ABB.Powertrain.Frontend.search')}
                                    disabled={isLoading}
                                />
                                {eventNameError &&
                                    <div className='search-error'>
                                        <IconCancel
                                            showCircle={true}
                                            color={colors.white}
                                            fillColor={colors.pomegranate}
                                            viewBox='0 0 50 50'
                                            width={18}
                                            height={18}
                                        /><span>{eventNameError}</span>
                                    </div>
                                }
                            </div>}

                        {(isSmartSensorTab || isDriveDCUTab) &&
                            <div className='filter-search-event-name'>
                                <label htmlFor='event-name-search' className='label-placeholder'>
                                    <span className='sr-only'>{translate('ABB.Powertrain.Frontend.SearchEventName')}</span>
                                </label>
                                <SearchBox
                                    id='event-name-search'
                                    className={eventNameError ? 'search-box--error' : ''}
                                    onChange={(searchTerm) => handleSearchFieldChangeWithValidation(FilterFields.EVENT_NAME, searchTerm)}
                                    value={filters?.eventName || ''}
                                    placeholder={translate('ABB.Powertrain.Frontend.SearchEventName')}
                                    disabled={isLoading}
                                />
                                {eventNameError ?
                                    <div className='search-error'>
                                        <IconCancel
                                            showCircle={true}
                                            color={colors.white}
                                            fillColor={colors.pomegranate}
                                            viewBox='0 0 50 50'
                                            width={18}
                                            height={18}
                                        /><span>{eventNameError}</span>
                                    </div>
                                    :
                                    <div className='error-placeholder' />
                                }
                            </div>}

                        {/* DCU serial number search - Smart Sensor DCU, Smart Sensor, Drive DCU */}
                        {(isGatewayTab || isSmartSensorTab || isDriveDCUTab) &&
                            <div className='filter-search-dcu-serial'>
                                <label>{translate('ABB.Powertrain.Frontend.searchEventBySerialNumberLabel')}</label>
                                <SearchBox
                                    className={dcuSerialNumberError ? 'search-box--error' : ''}
                                    onChange={(searchTerm) => handleSearchFieldChangeWithValidation(FilterFields.DCU_SERIAL_NUMBER, searchTerm)}
                                    value={filters?.dcuSerialNumber || ''}
                                    placeholder={translate('ABB.Powertrain.Frontend.searchEventBySerialNumberInputPlaceholder')}
                                    disabled={isLoading}
                                />
                                {dcuSerialNumberError &&
                                    <div className='search-error'>
                                        <IconCancel
                                            showCircle={true}
                                            color={colors.white}
                                            fillColor={colors.pomegranate}
                                            viewBox='0 0 50 50'
                                            width={18}
                                            height={18}
                                        /><span>{dcuSerialNumberError}</span>
                                    </div>
                                }
                            </div>
                        }

                        {(isAppTab || isGatewayTab) &&
                            <div className='filter-search-organization-name'>
                                <label htmlFor='organization-name-select'>{translate('ABB.Powertrain.Frontend.gatewayManagementOrganizationName')}</label>
                                <GeneralSelect
                                    inputId='organization-name-select'
                                    options={organizationOptions}
                                    placeholder={translate('ABB.Powertrain.Frontend.gatewayEventsSearchInputPlaceholder')}
                                    value={filters?.organizationId
                                        ? { value: filters.organizationId, label: filters.organizationName || '' }
                                        : null}
                                    onChange={(selected) => {
                                        handleSearchFieldChange(FilterFields.ORGANIZATION_ID, selected?.value ?? null);
                                        handleSearchFieldChange(FilterFields.ORGANIZATION_NAME, selected?.label || '');
                                        console.log('[GatewayEvents] Organization filter selected:', selected ? `${selected.label} (id: ${selected.value})` : '(cleared)');
                                    }}
                                    isClearable={true}
                                    isSearchable={true}
                                    isDisabled={isLoading}
                                />
                            </div>
                        }

                        {isAppTab &&
                            <div className='filter-search-user-email'>
                                <label htmlFor='user-email-search'>{translate('ABB.Powertrain.Frontend.gatewayManagementUserEmail')}</label>
                                <SearchBox
                                    id='user-email-search'
                                    className={userEmailError ? 'search-box--error' : ''}
                                    onChange={(searchTerm) => handleSearchFieldChangeWithValidation(FilterFields.USER_EMAIL, searchTerm)}
                                    value={filters?.userEmail || ''}
                                    placeholder={translate('ABB.Powertrain.Frontend.gatewayEventsSearchInputPlaceholder')}
                                    disabled={isLoading}
                                />
                                {userEmailError &&
                                    <div className='search-error'>
                                        <IconCancel
                                            showCircle={true}
                                            color={colors.white}
                                            fillColor={colors.pomegranate}
                                            viewBox='0 0 50 50'
                                            width={18}
                                            height={18}
                                        /><span>{userEmailError}</span>
                                    </div>
                                }
                            </div>
                        }

                        {isGatewayTab &&
                            <div className='filter-event-type'>
                                <label>{translate('ABB.Powertrain.Frontend.gatewayManagementFilterType')}</label>
                                <div className='event-types'>
                                    {[
                                        { key: GatewayEventTabType.ALL, label: 'ABB.Powertrain.Frontend.gatewayManagementFilterAll' },
                                        { key: GatewayEventTabType.LOG, label: 'ABB.Powertrain.Frontend.gatewayManagementFilterLog' },
                                        { key: GatewayEventTabType.REPORT, label: 'ABB.Powertrain.Frontend.gatewayManagementFilterReport' }
                                    ].map(({ key, label }) =>
                                        <button
                                            key={key}
                                            className={`event-type event-type-label${selectedTabType === key ? ' selected' : ''}`}
                                            onClick={() => !isLoading && handleSelectTabType(key)}
                                            disabled={isLoading}
                                        >
                                            {translate(label)}
                                        </button>
                                    )}
                                </div>
                            </div>
                        }

                        {isGatewayTab && selectedTabType === GatewayEventTabType.LOG &&
                            <div className='filter-event-type'>
                                <label>{translate('ABB.Powertrain.Frontend.gatewayManagementLevelFilter')}</label>
                                <GatewayEventTypeFilter
                                    selectedStates={displayedEventTypes}
                                    selectState={handleFilterByEventType}
                                    disabled={isLoading}
                                    showLabels={true}
                                    gatewayEventLogType={EventTypeGatewayApplicability.SMART_SENSOR_DCU}
                                />
                            </div>
                        }

                        {isGatewayTab && selectedTabType === GatewayEventTabType.REPORT &&
                            <div className='filter-event-type'>
                                <label>{translate('ABB.Powertrain.Frontend.gatewayManagementLevelFilter')}</label>
                                <GatewayEventReportTypeFilter
                                    selectedTypes={selectedReportTypes}
                                    onSelectType={handleFilterByReportType}
                                    disabled={isLoading}
                                />
                            </div>
                        }

                        <div className='reset-filter-actions ml-auto'>
                            <GeneralButton
                                type='normal'
                                onClick={handleResetFilters}
                                disabled={!hasSearchFilters && selectedTabType === GatewayEventTabType.ALL && selectedReportTypes.length === 0 || isLoading}
                                text={translate('ABB.Powertrain.Frontend.GatewayEventsresetFilter')}
                            />
                        </div>
                    </div>
                </div>
            }

            {/* Tab Content with proper loading handling */}
            <div className='tab-content'>
                {isLoading ?
                    <div className='gateway-events-loading'>
                        <LoadingIndicator />
                    </div>
                    :
                    <Suspense fallback={<div className='gateway-events-loading'><LoadingIndicator /></div>}>
                        <Switch>
                            <Route path={routes.GatewayManagementSmartSensorDCU}>
                                <GatewayEventTableComponent
                                    model={enhancedModel}
                                    actions={props.actions}
                                    tableKey='smartSensorDCUId'
                                    eventLogType={currentTab?.eventLogType}
                                />
                            </Route>
                            <Route path={routes.GatewayManagementSmartSensor}>
                                <GatewayEventTableComponent
                                    model={enhancedModel}
                                    actions={props.actions}
                                    tableKey='smartSensorId'
                                    eventLogType={currentTab?.eventLogType}
                                />
                            </Route>
                            <Route path={routes.GatewayManagementAPP}>
                                <GatewayEventTableComponent
                                    model={enhancedModel}
                                    actions={props.actions}
                                    tableKey='gatewayEventAppId'
                                    eventLogType={currentTab?.eventLogType}
                                />
                            </Route>
                            <Route path={routes.GatewayManagementDriveDCU}>
                                <GatewayEventTableComponent
                                    model={enhancedModel}
                                    actions={props.actions}
                                    tableKey='driveDCUId'
                                    eventLogType={currentTab?.eventLogType}
                                />
                            </Route>
                            {/* Default redirect to Smart Sensor DCU */}
                            <Route exact path={basePath}>
                                <Redirect to={routes.GatewayManagementSmartSensorDCU} />
                            </Route>
                            <Redirect from={`${basePath}/*`} to={routes.GatewayManagementSmartSensorDCU} />
                        </Switch>
                    </Suspense>
                }
            </div>

            {(isGatewayTab || isSmartSensorTab) &&
                <DownloadLogsModal
                    show={showDownloadLogsModal}
                    onClose={closeDownloadLogsModal}
                    tabType={currentTab?.eventLogType}
                    organizationOptions={organizationOptions}
                />
            }
        </div>
    );
};

GatewayEventsComponent.propTypes = {
    model: PropTypes.shape({
        filters: PropTypes.shape({
            eventTypes: PropTypes.array,
            eventName: PropTypes.string,
            gatewaySerialNumber: PropTypes.string,
            organizationName: PropTypes.string,
            organizationId: PropTypes.number,
            dcuSerialNumber: PropTypes.string,
            userEmail: PropTypes.string,
            dateSelection: PropTypes.shape({
                from: PropTypes.string,
                to: PropTypes.string
            })
        }),
        gatewayEventsLog: PropTypes.shape({
            events: PropTypes.array,
            nextCursor: PropTypes.string
        }),
        gatewayEventLogLoadingStatus: PropTypes.string,
        nextCursor: PropTypes.string,
        hasMoreData: PropTypes.bool,
        isLoadingMore: PropTypes.bool
    }).isRequired,
    actions: PropTypes.shape({
        setFilters: PropTypes.func.isRequired,
        resetFilters: PropTypes.func.isRequired,
        fetchGatewayEventsLog: PropTypes.func.isRequired,
        loadMoreGatewayEvents: PropTypes.func.isRequired
    }).isRequired
};

export default GatewayEventsComponent;
